<script setup lang="ts">
import { computed, nextTick, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import type { AnyAbility } from '@casl/ability'
import { useOrderStore } from '@/@vxapp/Orders/useOrderStore'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import MessagesDrawer from '@/components/Drawers/MessagesDrawer.vue'
import OrderListToolbar from '@/components/ServiceLists/Orders/OrderListToolbar.vue'
import OrderOperationsList from '@/components/ServiceLists/Orders/OrderOperationsList.vue'
import {
  OrderListSort,
  type OrderListQueryParams,
  type OrderListRecord,
  type OrderPaginationMeta,
} from '@/components/ServiceLists/Orders/orderListTypes'
import OrdersListInsights from '@/components/ServiceLists/OrdersListInsights.vue'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { ability } from '@/plugins/casl/composables/useAbility'

setPageTitle(PageMeta.Orders.List.Title)
definePage({ meta: PageMeta.Orders.List.Meta })

type OrderOperationsListApi = InstanceType<typeof OrderOperationsList>

const defaultQueryParams: OrderListQueryParams = {
  sort: OrderListSort.Newest,
  page: 1,
  limit: 10,
  search: undefined,
}

const allowedLimits = [10, 25, 50, 100]
const managedQueryKeys = Object.keys(defaultQueryParams) as Array<keyof OrderListQueryParams>
const orderAbility = ability as unknown as AnyAbility
const route = useRoute()
const router = useRouter()
const orderStore = useOrderStore()

const writableOrderStore = orderStore as unknown as {
  order: OrderListRecord | null
  queryParams: OrderListQueryParams
}

const orderOperationsList = useTemplateRef<OrderOperationsListApi>('orderOperationsList')
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)
const activeMessageOrder = shallowRef<OrderListRecord | null>(null)
let internallySyncedRouteQueryKey: string | null = null

const queryParams = computed<OrderListQueryParams>(() =>
  normalizeQueryParams(orderStore.queryParams as Record<string, unknown> | null)
)

const canReadCount = computed(() =>
  orderAbility.can(A.READ, Subject.ORDERS_PREVIEW_ALL, 'paginationMeta.entriesFound')
)

const canCreate = computed(() => orderAbility.can(A.CREATE, PageMeta.Orders.Add.Meta.subject))

function normalizeQueryValue(value: unknown) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
    ? String(normalizedValue)
    : undefined
}

function normalizePositiveInteger(value: unknown, fallback: number) {
  const parsedValue = Number.parseInt(normalizeQueryValue(value) ?? '', 10)

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeLimit(value: unknown) {
  const normalizedLimit = normalizePositiveInteger(value, defaultQueryParams.limit)

  return allowedLimits.includes(normalizedLimit) ? normalizedLimit : defaultQueryParams.limit
}

function normalizeQueryParams(source: Record<string, unknown> | null): OrderListQueryParams {
  const search = normalizeQueryValue(source?.search)?.trim()

  return {
    sort: normalizeQueryValue(source?.sort) || defaultQueryParams.sort,
    page: normalizePositiveInteger(source?.page, defaultQueryParams.page),
    limit: normalizeLimit(source?.limit),
    search: search || undefined,
  }
}

function queryParamsMatch(left: OrderListQueryParams, right: OrderListQueryParams) {
  return managedQueryKeys.every(key => left[key] === right[key])
}

function applyRouteQuery() {
  const currentQuery = normalizeQueryParams(
    orderStore.queryParams as Record<string, unknown> | null
  )

  const routeQuery = normalizeQueryParams(route.query)

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  writableOrderStore.queryParams = { ...currentQuery, ...routeQuery }

  return true
}

function buildRouteQuery(normalizedQueryParams: OrderListQueryParams = queryParams.value) {
  const nextQuery = { ...route.query }

  for (const key of managedQueryKeys) delete nextQuery[key]

  for (const key of managedQueryKeys) {
    const value = normalizedQueryParams[key]

    if (value !== defaultQueryParams[key] && value !== undefined) nextQuery[key] = String(value)
  }

  return nextQuery
}

async function syncRouteQuery(normalizedQueryParams: OrderListQueryParams = queryParams.value) {
  const nextQuery = buildRouteQuery(normalizedQueryParams)
  const nextQueryKey = JSON.stringify(nextQuery)

  if (JSON.stringify(route.query) === nextQueryKey) return

  internallySyncedRouteQueryKey = nextQueryKey
  await router.replace({ query: nextQuery })
}

async function refreshOrders(requestedQuery?: OrderListQueryParams) {
  await orderOperationsList.value?.fetchOrders(requestedQuery)
}

async function handleRefreshOrders() {
  await refreshOrders()
}

async function updateQueryParams(patch: Partial<OrderListQueryParams>) {
  const nextQueryParams = normalizeQueryParams({ ...queryParams.value, ...patch })

  writableOrderStore.queryParams = nextQueryParams

  if (isRouteQueryReady.value) await syncRouteQuery(nextQueryParams)

  await nextTick()
  await refreshOrders(nextQueryParams)
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({ limit: normalizeLimit(limit), page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

async function handleClear() {
  const nextQueryParams = normalizeQueryParams({ ...defaultQueryParams })

  writableOrderStore.queryParams = nextQueryParams

  if (isRouteQueryReady.value) await syncRouteQuery(nextQueryParams)

  await nextTick()
  await refreshOrders(nextQueryParams)
}

function handleLoaded(pagination: OrderPaginationMeta) {
  resultCount.value = pagination.entriesFound
}

function handleMessage(order: OrderListRecord) {
  activeMessageOrder.value = order
  writableOrderStore.order = order
  orderStore.isMessagesDrawerOpen = true
}

function handleNewOrder() {
  router.push({ name: PageMeta.Orders.Add.Name as 'apps-order-add' })
}

onMounted(async () => {
  await orderStore.init()
  applyRouteQuery()
  isRouteQueryReady.value = true
  await nextTick()
  await refreshOrders()
})

watch(
  () => route.query,
  async () => {
    const routeQueryKey = JSON.stringify(route.query)
    const wasSyncedInternally = internallySyncedRouteQueryKey === routeQueryKey
    const requestedQuery = normalizeQueryParams(route.query)

    if (wasSyncedInternally) internallySyncedRouteQueryKey = null

    if (!isRouteQueryReady.value || wasSyncedInternally || !applyRouteQuery()) return

    await nextTick()
    await refreshOrders(requestedQuery)
  },
  { deep: true }
)
</script>

<template>
  <section
    class="orders-workspace"
    data-testid="orders-workspace"
  >
    <MessagesDrawer
      v-if="activeMessageOrder"
      :key="activeMessageOrder.orderGUID"
      v-model:is-drawer-open="orderStore.isMessagesDrawerOpen"
      :service-g-u-i-d="activeMessageOrder.orderGUID"
      service-type="Orders"
    />

    <OrdersListInsights>
      <template #summary>
        <div class="orders-workspace__view-summary">
          <span>Current view</span>
          <strong v-if="canReadCount">{{
            resultCount === null ? '—' : resultCount.toLocaleString()
          }}</strong>
        </div>
      </template>
    </OrdersListInsights>

    <OrderListToolbar
      :query-params="queryParams"
      :result-count="canReadCount ? resultCount : null"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
    />

    <OrderOperationsList
      ref="orderOperationsList"
      :query-params="queryParams"
      @page-change="handlePageChange"
      @message="handleMessage"
      @loaded="handleLoaded"
    >
      <template #actions>
        <div class="orders-workspace__list-actions">
          <VBtn
            v-if="canCreate"
            data-testid="new-order-action"
            color="primary"
            prepend-icon="tabler-plus"
            @click="handleNewOrder"
          >
            New order
          </VBtn>
          <VBtn
            data-testid="refresh-orders-action"
            prepend-icon="tabler-refresh"
            variant="tonal"
            @click="handleRefreshOrders"
          >
            Refresh
          </VBtn>
        </div>
      </template>
    </OrderOperationsList>
  </section>
</template>

<style scoped lang="scss">
.orders-workspace {
  display: grid;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.orders-workspace__view-summary {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.orders-workspace__view-summary span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.orders-workspace__view-summary strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.orders-workspace__list-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

@media (max-width: 599px) {
  .orders-workspace {
    gap: 1rem;
  }

  .orders-workspace__view-summary {
    min-inline-size: 0;
  }

  .orders-workspace__list-actions {
    inline-size: 100%;
  }

  .orders-workspace__list-actions :deep(.v-btn) {
    flex: 1 1 8rem;
    min-block-size: 2.75rem;
  }
}
</style>
